import { useSyncExternalStore } from "react";
import { isAbortError } from "../../../api/http/httpClient";
import type { IncomingHostOffer } from "../../../api/threads/threadDecoders";
import { listIncomingHostOffers } from "../../../api/threads/threadHostingApi";
import { reportUiError, toUiError, type UiError } from "../../../errors/uiError";

/**
 * Host offers made to the signed-in user (Q-60; `GET /host-offers`), for the inbox in the
 * Threads section and the badge on its tab. Offers are not entries, so nothing announces
 * them live: the inbox reads them while shown, again every minute and when the window gets
 * the focus back, and drops an offer at once when the user decides it here or it expires.
 */
export interface HostOfferInbox {
  readonly offers: readonly IncomingHostOffer[];
  /** Milliseconds since the epoch of the last read or check; `null` before the first. */
  readonly checkedAt: number | null;
  readonly error: UiError | null;
}

export type HostOfferInboxEvent =
  | { readonly type: "read"; readonly offers: readonly IncomingHostOffer[]; readonly at: number }
  | { readonly type: "readFailed"; readonly error: UiError }
  /** Time passed: offers past their expiry leave. */
  | { readonly type: "checked"; readonly at: number }
  /** Accepted or declined here. */
  | { readonly type: "decided"; readonly offerId: string };

export const EMPTY_HOST_OFFER_INBOX: HostOfferInbox = { offers: [], checkedAt: null, error: null };

export function hostOfferInboxAfter(inbox: HostOfferInbox, event: HostOfferInboxEvent): HostOfferInbox {
  switch (event.type) {
    case "read":
      return { offers: event.offers, checkedAt: event.at, error: null };
    case "readFailed":
      return { ...inbox, error: event.error };
    case "checked":
      return { ...inbox, checkedAt: event.at };
    case "decided":
      return { ...inbox, offers: inbox.offers.filter((offer) => offer.offerId !== event.offerId) };
  }
}

/** The offers that can still be accepted at the last check. */
export function openHostOffers(inbox: HostOfferInbox): readonly IncomingHostOffer[] {
  const checkedAt = inbox.checkedAt;
  return checkedAt === null ? [] : inbox.offers.filter((offer) => Date.parse(offer.expiresAt) > checkedAt);
}

// ------------------------------------------------------------------ the shared inbox

const CHECK_EVERY_MILLISECONDS = 60_000;

let inbox = EMPTY_HOST_OFFER_INBOX;
const listeners = new Set<() => void>();
let reading: AbortController | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function apply(event: HostOfferInboxEvent): void {
  inbox = hostOfferInboxAfter(inbox, event);
  for (const listener of listeners) {
    listener();
  }
}

/** Read the offers again, for example after accepting or declining one elsewhere. */
export function refreshHostOfferInbox(): void {
  reading?.abort();
  const controller = new AbortController();
  reading = controller;
  listIncomingHostOffers(controller.signal).then(
    (offers) => {
      if (!controller.signal.aborted) {
        apply({ type: "read", offers, at: Date.now() });
      }
    },
    (caught: unknown) => {
      if (controller.signal.aborted || isAbortError(caught)) {
        return;
      }
      const error = toUiError(caught);
      reportUiError(error);
      apply({ type: "readFailed", error });
    },
  );
}

export function hostOfferDecided(offerId: string): void {
  apply({ type: "decided", offerId });
}

function check(): void {
  apply({ type: "checked", at: Date.now() });
  refreshHostOfferInbox();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    refreshHostOfferInbox();
    timer = setInterval(check, CHECK_EVERY_MILLISECONDS);
    window.addEventListener("focus", check);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      // Nobody shows the inbox (signed out, or the shell left): forget it.
      reading?.abort();
      reading = null;
      if (timer !== null) clearInterval(timer);
      timer = null;
      window.removeEventListener("focus", check);
      inbox = EMPTY_HOST_OFFER_INBOX;
    }
  };
}

export function useHostOfferInbox(): HostOfferInbox {
  return useSyncExternalStore(subscribe, () => inbox);
}

/**
 * The one offer whose card shows (OW-09): the link's, else the one waiting in the inbox, at once. The card's Take
 * over accepts it, so a Run shows one Take over with one effect (UI wording brief, item 1). Pure.
 */
export function shownHostOffer(addressed: string | null, inbox: string | null): string | null {
  return addressed ?? inbox;
}
